import { format } from 'date-fns';

/** The fields of a response the poll history reads; the API's response type is assignable to it. */
export type PollHistoryResponse = {
  surveyGroupId: string;
  surveyName: string;
  surveyType: string;
  submittedAt: string;
  answers: Record<string, unknown>;
  resultsUrl: string | null;
};

export type AnsweredPoll = {
  surveyGroupId: string;
  name: string;
  submittedAt: string;
  answers: Record<string, unknown>;
  resultsUrl: string | null;
};

/**
 * One entry per poll the respondent answered, newest first.
 *
 * A poll answered more than once, across versions or by submitting again,
 * lists once with its newest response. That is the response its results count,
 * so its answers are the ones to mark, and its name and results link are the
 * current ones.
 *
 * Filters on type as well as asking the API for polls only: an API that
 * predates the filter ignores it and returns every survey, without a type,
 * and those must not surface as polls.
 */
export function getAnsweredPolls(responses: readonly PollHistoryResponse[]): AnsweredPoll[] {
  const newestFirst = responses
    .filter((response) => response.surveyType === 'poll')
    .sort((first, second) => second.submittedAt.localeCompare(first.submittedAt));

  const listedGroupIds = new Set<string>();

  return newestFirst.reduce<AnsweredPoll[]>((polls, response) => {
    if (listedGroupIds.has(response.surveyGroupId)) {
      return polls;
    }

    listedGroupIds.add(response.surveyGroupId);

    polls.push({
      surveyGroupId: response.surveyGroupId,
      name: response.surveyName,
      submittedAt: response.submittedAt,
      answers: response.answers,
      resultsUrl: response.resultsUrl,
    });

    return polls;
  }, []);
}

/**
 * The results questions this respondent answered. A question a show-if rule
 * hid from them has no answer, so it drops out without loading the schema of
 * a version that may since have been archived. An optional question they
 * skipped drops out with it.
 */
export function getAnsweredQuestionNames(
  questionNames: readonly string[],
  answers: Record<string, unknown>
): Set<string> {
  return new Set(questionNames.filter((name) => answers[name] !== undefined));
}

export function formatAnsweredDate(submittedAt: string): string {
  return `Answered ${format(new Date(submittedAt), 'MMM d, yyyy')}`;
}

export function formatAnsweredPollCount(count: number): string {
  return `${count} ${count === 1 ? 'poll' : 'polls'} answered`;
}
